import type { BtlApplication } from '@api/btlApplicationForm/types';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import { App } from 'antd';
import { firmApi } from '@api/firm/firm.api';
import { userApi } from '@api/user/userApi';
import {
  getAdjacentOoCaseSidebarItemId,
  getAdjacentOoCaseSidebarUrlSection,
  getOoCaseSidebarNavigationLabel,
} from '@components/CaseSidebar/navigation/ooCaseSidebarNavigation';
import type { OoCaseSidebarContext } from '@components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { FormValidationContext } from '@hooks/useBtlFormValidation';
import { invalidateBtlApplicationData } from '@pages/BtlApplicationFormPage/btl-utils';
import { buildUpdateFinanceAndPropertyDto } from '@pages/BtlApplicationFormPage/FinancingAndProperty/buildUpdateFinanceAndPropertyDto';
import { propertyFormSchema } from '@pages/BtlApplicationFormPage/FinancingAndProperty/formSchema';
import {
  hydrateFinancingAndPropertyFormValues,
  mapApplicationToFormValues,
} from '@pages/BtlApplicationFormPage/FinancingAndProperty/mapApplicationToFormValues';
import { makeFormReadonly } from '@pages/BtlApplicationFormPage/makeFormReadonly';
import { useBtlApplicationInProgressById } from '@queries/btlApplications/useApplicationInProgressById/useBtlApplicationInProgressById';
import { useBrokerFirm } from '@queries/broker/useBrokerFirm';
import { useAuth } from '@providers/AuthProvider/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { standardFormValidationProps } from '@common/formValidation';
import {
  buildHouseholdPayloadWithPropertyOutgoings,
  mapDeclaredExpenditureToPropertyOutgoingsFormValues,
} from './mapPropertyOutgoings';
import { ooPropertyOutgoingsFormSchema } from './ooPropertyOutgoingsFormSchema';

interface UseOoCasePropertyFormParams {
  caseId: string;
  sidebarContext: OoCaseSidebarContext;
}

export function isOoPropertyFormEditable(caseData?: BtlApplication): boolean {
  return caseData?.status === 'DIP_ACCEPTED';
}

function getPropertyFormParams(caseData?: BtlApplication, caseId?: string, householdId?: string) {
  const property = caseData?.applicationProperty?.property;

  return {
    caseId,
    propertyId: property?.id,
    householdId,
    status: caseData?.status,
    tenure: caseData?.applicationProperty?.tenure,
    purchasePrice: property?.purchasePrice,
    propertyType: property?.propertyType,
    postcode: property?.address?.postcode,
  };
}

export function useOoCasePropertyForm({ caseId, sidebarContext }: UseOoCasePropertyFormParams) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { formatMessage } = useIntl();
  const { isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const { data: caseData, isLoading: isCaseLoading } = useBtlApplicationInProgressById(caseId);
  const { data: firm, isLoading: isFirmLoading } = useBrokerFirm();
  const queryClient = useQueryClient();
  const formValidationContext = useContext(FormValidationContext);
  const formSectionParam = searchParams.get('section') ?? CaseSidebarItemId.PROPERTY;
  const propertyId = caseData?.applicationProperty?.property?.id;
  const isFormEditable = isOoPropertyFormEditable(caseData);
  const nextSection = getAdjacentOoCaseSidebarUrlSection(formSectionParam, sidebarContext, 'next') ?? '';

  const { data: households, isLoading: isHouseholdsLoading } = useQuery(
    ['btl-application-households', caseId],
    () => btlApplicationFormApi.getHouseholds(caseId),
    {
      enabled: Boolean(caseId) && !isCaseLoading,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const household = households?.[0];
  const householdId = household?.id;
  const formParams = useMemo(
    () => getPropertyFormParams(caseData, caseId, householdId),
    [caseData, caseId, householdId],
  );
  const formKey = useMemo(() => JSON.stringify(formParams), [formParams]);

  if (!formValidationContext) {
    throw new Error('OO property form must be used within a FormValidationProvider');
  }

  const { setIsSubmitting } = formValidationContext;

  if (isCaseLoading || isFirmLoading || isHouseholdsLoading) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    firm,
    isFormEditable,
    isDipAcceptedOrLater: true,
    variant: 'oo' as const,
  };

  const formSchema = [...propertyFormSchema(schemaProps), ...ooPropertyOutgoingsFormSchema({ formatMessage })];
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    isFormEditable,
    formKey,
    footerProps: {
      previousSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'previous'),
      nextSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'next'),
      previousLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'previous'),
      nextLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'next'),
    },
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      name: 'oo_property',
      ...standardFormValidationProps,
      params: formParams,
      request: async () => {
        let brokerReferrerId: string | undefined;

        if (isSignedIn) {
          try {
            const user = await userApi.getUser();
            if (user?.id) {
              const brokerReferrer = await firmApi.getBrokerReferrerByUserId(user.id);
              brokerReferrerId = brokerReferrer.id;
            }
          } catch {
            brokerReferrerId = undefined;
          }
        }

        const baseValues = await hydrateFinancingAndPropertyFormValues(caseData, firm, brokerReferrerId);

        return {
          ...baseValues,
          propertyOutgoings: mapDeclaredExpenditureToPropertyOutgoingsFormValues(household?.declaredExpenditure),
        };
      },
      onFinish: async (values: Record<string, unknown>) => {
        const { propertyOutgoings, ...propertyValues } = values;
        const baseValues = mapApplicationToFormValues(caseData, firm);
        const mergedValues = { ...baseValues, ...propertyValues };

        try {
          setIsSubmitting(true);

          const updateCaseDto = buildUpdateFinanceAndPropertyDto({
            caseId,
            propertyId,
            mergedValues,
            data: caseData,
            firm,
          });
          await btlApplicationFormApi.updateFinanceAndProperty(caseId, updateCaseDto);

          const householdPayload = buildHouseholdPayloadWithPropertyOutgoings(
            household,
            propertyOutgoings as Record<string, unknown> | undefined,
          );

          if (householdId) {
            await btlApplicationFormApi.editHousehold(caseId, householdId, householdPayload);
          } else {
            const createdHousehold = await btlApplicationFormApi.createHousehold(caseId, householdPayload);
            const individuals = caseData?.borrower.individuals ?? [];
            const borrowerId = caseData?.borrower.id;

            if (borrowerId && individuals.length > 0) {
              await Promise.all(
                individuals.map(individual =>
                  btlApplicationFormApi.linkIndividualToHousehold(caseId, borrowerId, individual.id, {
                    householdId: createdHousehold.id,
                  }),
                ),
              );
            }
          }

          await invalidateBtlApplicationData(queryClient, caseId);
          await queryClient.invalidateQueries({ queryKey: ['btl-application-households', caseId] });

          if (nextSection) {
            navigate({ search: `?section=${nextSection}` });
          }
        } catch (error: unknown) {
          console.error(error);
          message.error(error instanceof Error ? error.message : 'Failed to save property');
        } finally {
          setIsSubmitting(false);
        }
      },
    },
  };
}
